import { RSI_TIMEFRAMES, type RsiAnalysis, type Timeframe } from '@pipyar/core';
import { cn } from '@/lib/utils';

export const BIAS_TEXT: Record<'bullish' | 'bearish' | 'neutral', string> = {
  bullish: 'text-bull',
  bearish: 'text-bear',
  neutral: 'text-muted-foreground',
};

export const BIAS_CHIP: Record<'bullish' | 'bearish' | 'neutral', string> = {
  bullish: 'bg-bull/15 text-bull',
  bearish: 'bg-bear/15 text-bear',
  neutral: 'bg-muted text-muted-foreground',
};

/** Solid chips for a zone backed by a matching divergence. */
const STRONG_CHIP: Record<'bullish' | 'bearish', string> = {
  bullish: 'bg-bull text-white',
  bearish: 'bg-bear text-white',
};

/** Chip classes for a verdict, solid when it is "very" bullish/bearish. */
export function biasChip(a: Pick<RsiAnalysis, 'bias' | 'strong'> | undefined): string {
  if (!a) return BIAS_CHIP.neutral;
  return a.strong && a.bias !== 'neutral' ? STRONG_CHIP[a.bias] : BIAS_CHIP[a.bias];
}

/** "very bullish", "bearish", "neutral" … */
export function biasLabel(a: Pick<RsiAnalysis, 'bias' | 'strong'> | undefined): string {
  if (!a) return '—';
  return a.strong && a.bias !== 'neutral' ? `very ${a.bias}` : a.bias;
}

/** Colour the RSI number by its zone, not by the overall bias. */
function zoneColor(a: RsiAnalysis): string {
  if (a.zone === 'overbought') return 'text-bear';
  if (a.zone === 'oversold') return 'text-bull';
  return 'text-foreground';
}

interface Props {
  rows: RsiAnalysis[] | undefined;
  isLoading?: boolean;
  active?: Timeframe;
  onSelect?: (tf: Timeframe) => void;
  className?: string;
}

/**
 * RSI value and verdict per timeframe.
 *
 * Shared by the RSI page sidebar and (in aggregate) the dashboard widget, so the
 * two cannot drift into reporting different readings for the same series.
 */
export function RsiSummaryGrid({ rows, isLoading = false, active, onSelect, className }: Props) {
  const byTimeframe = new Map((rows ?? []).map((r) => [r.timeframe, r]));

  return (
    <div className={cn('grid grid-cols-2 gap-2', className)}>
      {RSI_TIMEFRAMES.map((tf) => {
        const row = byTimeframe.get(tf);
        const isActive = active === tf;
        const divergences = row?.divergences.length ?? 0;

        return (
          <button
            key={tf}
            type="button"
            disabled={!onSelect}
            onClick={() => onSelect?.(tf)}
            aria-current={isActive ? 'true' : undefined}
            className={cn(
              'rounded-xl border p-2.5 text-start transition-colors',
              onSelect ? 'cursor-pointer hover:border-primary/40 hover:bg-muted/60' : 'cursor-default',
              isActive ? 'border-primary/50 bg-primary/8' : 'border-border bg-surface/50',
            )}
          >
            <div className="flex items-baseline justify-between">
              <span
                className={cn('text-xs font-semibold', isActive ? 'text-primary' : 'text-foreground')}
              >
                {tf}
              </span>
              {isLoading && !row ? (
                <span className="h-4 w-8 animate-pulse rounded bg-muted" />
              ) : (
                <span className={cn('tabular text-sm font-semibold', zoneColor(row!))}>
                  {row?.value ?? '—'}
                </span>
              )}
            </div>

            <div className="mt-1.5 flex items-center gap-1.5">
              <span
                className={cn(
                  'rounded px-1 py-px text-[10px] font-medium capitalize',
                  biasChip(row),
                )}
              >
                {biasLabel(row)}
              </span>
              {divergences > 0 && (
                <span
                  className="tabular text-[10px] text-muted-foreground"
                  title={`${divergences} divergence${divergences === 1 ? '' : 's'} in view`}
                >
                  ÷{divergences}
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
